import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import { EmployeeEditSkeleton } from '../../components/directory-skeletons/directory-skeletons';
import { EmployeeCreateForm } from '../../components/employee-form/employee-create-form';
import { EmployeeEditTabs } from '../../components/employee-form/employee-edit-tabs';
import { useGetEmployeeById } from '../../hooks/use-employees';
import { Employee, EmployeesPagingResult } from '../../types/employee.types';
import { employeeName } from '../../schemas/display-name';
import { APP_PATHS } from '@/constant/app-paths';

/** The page title with a back arrow (to the employee's details when editing, else the directory). */
const FormHeader = ({ title, backTo }: { title: ReactNode; backTo: string }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="flex items-center gap-2">
      <Button
        variant="ghost"
        size="icon"
        className="shrink-0"
        aria-label={t('BACK')}
        title={t('BACK')}
        onClick={() => navigate(backTo)}
      >
        <ArrowLeft className="size-5" />
      </Button>
      <h1 className="min-w-0 truncate text-lg font-semibold">{title}</h1>
    </div>
  );
};

/** `/people/new` adds a person with the minimum; `/people/:employeeId/edit` edits one tab by tab. */
export const EmployeeFormPage = () => {
  const { t } = useTranslation();
  const { employeeId } = useParams<{ employeeId: string }>();
  const isEditMode = Boolean(employeeId) && employeeId !== 'new';

  const { data, isLoading } = useGetEmployeeById(isEditMode ? employeeId : undefined);
  const employee: Employee | undefined = (data as { Employees?: EmployeesPagingResult })?.Employees
    ?.items?.[0];

  if (!isEditMode) {
    return (
      <div className="flex w-full flex-col gap-4">
        <FormHeader title={t('ADD_EMPLOYEE')} backTo={APP_PATHS.PEOPLE} />
        <EmployeeCreateForm />
      </div>
    );
  }

  const backToDetails = `${APP_PATHS.PEOPLE}/${employeeId}`;

  if (isLoading || !employee) {
    return (
      <div className="flex w-full flex-col gap-4">
        <FormHeader title={t('EDIT_EMPLOYEE')} backTo={backToDetails} />
        {isLoading ? (
          <EmployeeEditSkeleton />
        ) : (
          <p className="text-sm text-muted-foreground">{t('EMPLOYEE_NOT_FOUND')}</p>
        )}
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <FormHeader
        title={`${t('EDIT_EMPLOYEE')}: ${employeeName(employee)}`}
        backTo={backToDetails}
      />
      <EmployeeEditTabs key={employee.ItemId} employee={employee} />
    </div>
  );
};
